import { useEffect, useRef, type RefObject } from "react";
import log from "logbeacon";
import { drawPieChart, type PieChartOptions } from "../components/createChart";

/**
 * 饼图 React Hook 封装
 * @param ref - 图表容器的 ref
 * @param options - 饼图配置项（不包含 el）
 * @returns void
 *
 * @example
 * ```tsx
 * const chartRef = useRef<HTMLDivElement>(null);
 *
 * usePieChart(chartRef, {
 *   data: [
 *     { key: 'btc', label: 'BTC', value: 40, color: '#00D4FF', hiddenColor: '#444' },
 *     { key: 'eth', label: 'ETH', value: 30, color: '#7B61FF', hiddenColor: '#444' }
 *   ],
 *   centerContent: { value: 12, label: 'Total' },
 * });
 *
 * return <div ref={chartRef} style={{ width: 300, height: 300 }} />;
 * ```
 */
export const usePieChart = (
  ref: RefObject<HTMLElement | null>,
  options: Omit<PieChartOptions, "el">,
) => {
  const instanceRef = useRef<{
    cleanup: () => void;
    setHoverKey: (key: string | null) => void;
  } | null>(null);

  // 主 effect：创建图表实例
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    log.debug(
      "[usePieChart] ref.current:",
      ref.current,
      ref.current.clientHeight,
    );

    // 清理旧实例
    if (instanceRef.current) {
      instanceRef.current.cleanup();
    }

    // 创建新实例
    const instance = drawPieChart({
      ...options,
      el: ref.current,
    });
    instanceRef.current = instance;

    return () => {
      if (instanceRef.current) {
        instanceRef.current.cleanup();
        instanceRef.current = null;
      }
    };
  }, [options.data, options.centerContent, ref, options]);

  // 返回控制方法
  return {
    setHoverKey: (key: string | null) => {
      if (instanceRef.current) {
        instanceRef.current.setHoverKey(key);
      }
    },
  };
};
